/**
 * Enrich TipTap nodes with metadata from `dataContent`, including default values.
 *
 * New array format example:
 * [
 *   { key: 'role', kind: 'select', required: true, options: [{ value: 'physician' }], value: 'nurse' },
 *   { key: 'topic', kind: 'input', placeholder: 'Specific topic', required: true, value: 'Diabetes' },
 * ]
 *
 * Old format example:
 * {
 *   input: { topic: { placeholder: '...', required: true, value: 'Diabetes' } },
 *   select: { role: { required: true, values: [{ label: 'Doctor', value: 'Doctor' }], value: 'Nurse' } },
 * }
 */

export function applyDataContentToEditor(editor, dataContent) {
  if (!editor) return;

  if (
    !dataContent ||
    (Array.isArray(dataContent) && dataContent.length === 0) ||
    (typeof dataContent === 'object' && Object.keys(dataContent).length === 0)
  ) {
    // Clear the editor if no dataContent
    editor.commands.clearContent(true);
    return;
  }

  let normalized = [];

  if (Array.isArray(dataContent)) {
    normalized = dataContent.map((item) => ({
      key: item.key,
      kind: item.kind || 'input',
      placeholder: item.placeholder || item.label || '',
      required: !!item.required,
      multiple: item.multiple,
      value: item.value ?? item.defaultValue ?? '',
      options:
        Array.isArray(item.options) && item.kind === 'select'
          ? item.options.map((opt) => {
              if (typeof opt === 'string') return opt;
              if (opt && typeof opt === 'object') return opt.label || opt.value || '';
              return '';
            })
          : [],
    }));
  } else if (typeof dataContent === 'object') {
    for (const [kind, fields] of Object.entries(dataContent)) {
      for (const [key, field] of Object.entries(fields)) {
        normalized.push({
          key,
          kind,
          placeholder: field.placeholder || field.label || '',
          required: !!field.required,
          multiple: field.multiple,
          value: field.value ?? field.defaultValue ?? '',
          options:
            kind === 'select'
              ? (field.values || []).map((opt) => (typeof opt === 'object' ? opt.label || opt.value : opt))
              : [],
        });
      }
    }
  }

  if (!normalized.length) {
    editor.commands.clearContent(true);
    return;
  }

  const { state } = editor;
  const tr = state.tr;
  let changed = false;

  state.doc.descendants((node, pos) => {
    if (!node.attrs?.variable) return;

    const variable = node.attrs.variable;
    const field = normalized.find((f) => f.key === variable);
    if (!field) return;

    // Handle multiple values: join array into comma-separated lowercase string
    let valueToSet = '';
    if (Array.isArray(field.value)) {
      valueToSet = field.value.map((v) => String(v).toLowerCase()).join(',');
    } else if (typeof field.value === 'string') {
      valueToSet = field.value.toLowerCase();
    } else {
      valueToSet = '';
    }

    const updatedAttrs = {
      ...node.attrs,
      variable,
      placeholder: field.placeholder || field.label || '',
      required: field.required || false,
      multiple: field.multiple || false,
      value: valueToSet,
    };

    if (field.kind === 'select') {
      updatedAttrs.options = field.options;
    }

    // Only change if something actually differs
    const hasChanged = Object.keys(updatedAttrs).some((k) => updatedAttrs[k] !== node.attrs[k]);

    if (hasChanged) {
      changed = true;
      tr.setNodeMarkup(pos, undefined, updatedAttrs);
    }
  });

  if (changed) {
    editor.view.dispatch(tr);
  }
}

export const extractVariablesFromHTML = (html) => {
  if (!html) return [];
  const regex = /data-variable="([^"]+)"\s+data-value="([^"]*)"/g;
  const variables = [];
  let match;
  while ((match = regex.exec(html)) !== null) {
    variables.push({ key: match[1], value: match[2] || '' });
  }
  return variables;
};

export const getRequiredFieldKeys = (dataContent) => {
  if (!dataContent) return [];

  if (Array.isArray(dataContent)) {
    // New format
    return dataContent
      .filter((f) => f && f.required)
      .map((f) => f.key)
      .filter(Boolean);
  } else if (typeof dataContent === 'object') {
    // Old format
    const keys = [];
    for (const group of Object.values(dataContent)) {
      if (!group) continue;
      for (const [key, field] of Object.entries(group || {})) {
        if (field && field.required) keys.push(key);
      }
    }
    return keys;
  }
  return [];
};

export const hasEmptyRequiredFields = (variables = [], requiredKeys = []) => {
  if (!variables.length || !requiredKeys.length) return false;

  return requiredKeys.some((key) => {
    const field = variables.find((v) => v.key === key);
    if (!field) return false; // If the key doesn’t exist, ignore it
    return !field.value || field.value.trim() === '';
  });
};

export const disableInteractiveElements = (editor) => {
  const root = editor.view.dom;

  // Disable variableSelect custom dropdowns
  root.querySelectorAll('span.variable-select').forEach((el) => {
    // Disable main display (so it can’t open)
    const display = el.querySelector('.variable-select-control');
    if (display) {
      display.setAttribute('tabindex', '-1');
    }

    // Hide or disable dropdown options
    const dropdown = el.querySelector('.variable-select-dropdown');
    if (dropdown) {
      dropdown.style.display = 'none'; // ensure hidden when disabled
    }

    // Remove chevron if it exists
    const chevron = el.querySelector('.chevron');
    if (chevron) {
      chevron.remove();
    }
  });

  // Disable variableInput spans and handle multi-line underlines
  root.querySelectorAll('span.variable-input').forEach((el) => {
    const editable = el.querySelector('[contenteditable="true"]');
    if (!editable) return;

    editable.contentEditable = 'false';
    editable.setAttribute('tabindex', '-1');
  });
};
